"use client";

import { useTranslations } from "next-intl";
import { Gauge } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/format-datetime";
import { BAND_TEXT, ratingBand } from "../lib/rating-band";
import type { JudgeProblemRating } from "../types";

/**
 * `EX-PRT01`, `docs/29` §7.7: a problem's rating from its latest rated round,
 * beside the setter's difficulty (never instead of it). The number wears the
 * rating band's colour a contestant's rating wears, because it is on the same
 * scale: a problem rated 1500 is one a 1500 contestant solves about half the
 * time. The tooltip says that and names the round; a `private` round is not
 * named.
 *
 * The tooltip is drawn by CSS on hover and, where the chip is focusable, on
 * focus; the sentence is also in the chip for a screen reader, since inside a
 * library row the chip sits in a link and must not take focus itself.
 */
export function ProblemRatingChip({
  rating,
  locale,
  focusable = false,
  className,
}: {
  rating: JudgeProblemRating;
  locale: string;
  /** The problem head's chip takes focus; a library row's sits inside its link. */
  focusable?: boolean;
  className?: string;
}) {
  const t = useTranslations("judge.catalog");
  const value = formatNumber(rating.value, locale);
  const round = rating.round ? (locale === "bn" && rating.round.title_bn ? rating.round.title_bn : rating.round.title_en) : null;
  const tip = round ? t("ratingTip", { rating: value, round }) : t("ratingTipUnnamed", { rating: value });

  return (
    <span
      data-testid="problem-rating"
      tabIndex={focusable ? 0 : undefined}
      className={cn(
        "group/rating relative inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-white pl-1.5 pr-2 font-mono text-[12.5px] font-bold tabular-nums ring-1 ring-inset ring-neutral-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        BAND_TEXT[ratingBand(rating.value)],
        className,
      )}
    >
      <Gauge className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span aria-hidden>{value}</span>
      <span className="sr-only">{tip}</span>
      <span
        role="tooltip"
        aria-hidden
        className="pointer-events-none invisible absolute bottom-full right-0 z-20 mb-2 w-max max-w-[260px] whitespace-normal rounded-lg bg-neutral-900 px-3 py-2 text-left font-sans text-[12.5px] font-medium leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/rating:visible group-hover/rating:opacity-100 group-focus-visible/rating:visible group-focus-visible/rating:opacity-100 motion-reduce:transition-none"
      >
        {tip}
      </span>
    </span>
  );
}
